import React, { useEffect, useRef } from "react";
import "../styles/AnavuLandingContent.css";
import SlidingVideos from "./SlidingVideos";
import sectionIntroImage from "../images/section_intro.png";
import sectionFeaturesImage1 from "../images/section_features1.png";
import sectionFeaturesImage2 from "../images/section_features2.jpg";
import sectionFeaturesImage3 from "../images/section_features3.png";
import anavuForEducators from "../videos/anavuForEducators.mp4";
import anavuForLearners from "../videos/anavuForLearners.mp4";
import partner1 from "../images/partner1.png";
import partner2 from "../images/partner2.png";
import partner3 from "../images/partner3.png";
import partner4 from "../images/partner4.png";
import youtube_icon from "../images/youtube_icon.png";
import anavuForStereoscopy from "../videos/anavuForStereoscopy.mp4";

const AnavuLandingPage = () => {
  const videoRefs = useRef([]); // Reference to store video elements

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.5, // Trigger when 50% of the element is visible
    };

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        const video = videoRefs.current[entry.target.dataset.index];
        if (entry.isIntersecting) {
          // Play the video when it comes into view
          video.play().catch((error) => {
            console.error("Video playback failed:", error);
          });
        } else {
          // Pause the video when it goes out of view
          video.pause();
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, options);

    // Observe each video element
    videoRefs.current.forEach((video) => {
      if (video) observer.observe(video);
    });

    return () => {
      // Cleanup the observer on component unmount
      observer.disconnect();
    };
  }, []);

  return (
    <div className="anavuLandingContent">
      <SlidingVideos />

      <section className="intro-section">
        <div className="intro-text">
          <div className="intro-heading">
            AnaVu- Visualisation tool for Medical Anatomy
          </div>
          <p className="intro-description">
            Our goal is to provide an anatomical visualization tool aimed at
            classroom teaching, presenting in-vivo anatomical data alongside 3D
            structures to help students better understand inter-structure
            relations. The tool visualizes accurate 3D structures extracted from
            real-world data registered alongside volumetric radiological slices
            to provide in-context information for better learning. Multiple
            modalities such as MRI, CT scans alongside tractography and venogram
            data are supported. Multimedia support in the form of animations,
            images, and videos help teachers better explain the complex
            anatomical landscape.
          </p>
        </div>
        <div className="intro-image-container">
          <div className="intro-image">
            <img src={sectionIntroImage} alt="Intro MRI" />
          </div>
        </div>
      </section>

      <section className="features-section">
        <div className="feature-item">
          <div className="feature-item-img">
            <img src={sectionFeaturesImage1} alt="Load annotated MRI/CT" />
          </div>
          <p>Load annotated MRI/CT</p>
        </div>
        <div className="feature-item">
          <div className="feature-item-img">
            <img
              src={sectionFeaturesImage3}
              alt="3D Models of Target Structures"
            />
          </div>
          <p>3D models of target structures</p>
        </div>
        <div className="feature-item">
          <div className="feature-item-img">
            <img
              src={sectionFeaturesImage2}
              alt="Visualize 3D Structures with 2D Cross-Sections"
            />
          </div>
          <p>Visualize 3D structures with 2D cross-sections</p>
        </div>
      </section>

      <section className="stereoscopy-section">
        <div className="stereoscopy-image-container">
          <div className="stereoscopy-image">
            <video
              ref={(el) => (videoRefs.current[0] = el)}
              data-index="0"
              src={anavuForStereoscopy}
              alt="AnaVu for Stereoscopy"
              loop
              // Start
              playsInline // Add this for mobile compatibility
            />
          </div>
        </div>
        <div className="stereoscopy-text">
          <div className="section-heading">AnaVu : Stereoscopy</div>
          <div className="section-description">
            AnaVu also supports stereo projection permitting the 3D models to be
            viewed in full stereo. This type of projection is suited to a
            classroom and gives a rich immersive experience of the anatomy to
            learners and teachers.
            <br /> Use of stereo projection with two projectors in a classroom
            helps students wearing passive 3D glasses to identify even subtle
            depth differences between sub-structures. With support for multiple
            projectors, touch enabled displays and upcoming VR support, AnaVu
            provides a flexible way to teach anatomy in a fun and interactive
            manner.
          </div>
        </div>
      </section>

      <section className="educators-section">
        <div className="educators-image-container">
          <div className="educators-image">
            <video
              ref={(el) => (videoRefs.current[1] = el)}
              data-index="1"
              src={anavuForEducators}
              alt="AnaVu for Educators"
              loop
              // Start
              playsInline // Add this for mobile compatibility
            />
          </div>
        </div>
        <div className="educators-text">
          <div className="section-heading">AnaVu for Educators</div>
          <div className="section-description">
            AnaVu, a powerful anatomy teaching aid, is designed with extensive
            feedback from anatomy teachers for ease of use and easy integration
            with ongoing lectures. The software allows easy control of on-screen
            information using only the mouse while giving the teacher full
            control over the lecture. This helps reduce the reliance on cadaver
            dissection to learn 3D relations. Blood flow and fiber tracts that
            are difficult to view in a cadaver can be easily visualized using
            AnaVu.
            <br />
            With AnaVu’s lesson creation tools, the teacher can create custom
            lessons using multiple modalities. The teacher has full control over
            the presentation in terms of animation playbacks using different
            timelines, slice control using sliders, and full pan, zoom, tilt
            control for the 3D structures in order to accurately present the
            on-screen information both in terms of 3D and its volumetric
            counterpart to maximize learning.
          </div>
        </div>
      </section>

      <div className="testimonial educators-testimonial">
        <p>
          As an educator, I've seen my students engage with the material like
          never before. The interactive features really bring anatomy to life. –
          Anatomy Professor
        </p>
      </div>

      <section className="learners-section">
        <div className="learners-image-container">
          <div className="learners-image">
            <video
              ref={(el) => (videoRefs.current[2] = el)}
              data-index="2"
              src={anavuForLearners}
              alt="AnaVu for Learners"
              loop
              // Start
              playsInline // Add this for mobile compatibility
            />
          </div>
        </div>
        <div className="learners-text">
          <div className="section-heading">AnaVu for Learners</div>
          <div className="section-description">
            AnaVu helps visualize anatomy in full 3D making it easier to learn
            without relying on building mental models and memorization. The
            extensive manipulations make it fun to learn anatomy via free
            explorations which are not possible with a cadaver. Students also
            benefit from AnaVu because of its use of real-world data as opposed
            to graphical representations used in other software. Since AnaVu
            uses real-world data and 3D structures, the users can interact and
            engage with the tool to visualize complex structures, explore
            anatomical relations, and enhance their understanding of the human
            body.
          </div>
          <div className="anavuChannel">
            <a
              href="https://www.youtube.com/@AnaVu-Hyd"
              target="_blank"
              rel="noreferrer"
            >
              <div className="youtube_icon">
                <img src={youtube_icon} alt="youtube icon" />
              </div>
              <div className="anavuChannel_text">Anavu Channel</div>
            </a>
          </div>
        </div>
      </section>

      <div className="testimonial learners-testimonial">
        <p>
          AnaVu has changed the way I learn anatomy! I feel more confident and
          engaged in my studies. – Medical Student
        </p>
      </div>

      <section className="partners-section">
        <h2 className="partners-title">Our Partners</h2>
        <div className="partners-logos">
          <img src={partner1} alt="Osmania University" />
          <img src={partner2} alt="Bhaskara Medical College" />
          <img src={partner3} alt="Sree Chakra" />
          <img src={partner4} alt="Trivandrum Medical College" />
        </div>
      </section>
    </div>
  );
};

export default AnavuLandingPage;
